import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Search,
  ClipboardCheck,
  Package,
  Clock,
  CheckCircle,
  Minus,
  Plus,
  AlertCircle,
  Loader2,
  ArrowLeft,
  Filter,
  Eye,
  PlusCircle,
  ChevronDown,
  ChevronRight,
} from 'lucide-react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { useToast } from '@/components/ui/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import DataTable from '@/components/DataTable';
import {
  registrarLlegada,
  verificarRecoleccion,
  fetchEntregasClientes,
} from '@/services/entregasClientes.service';
import {
  getClientAssignedGarments,
  getClientGarments,
} from '@/pages/parametrizacion/clientGarments/Services/clientGarments.services';

const VerificacionRopaSuciaPage = () => {
  const { idLavanderia } = useParams();
  const { toast } = useToast();
  const { user } = useAuth();

  // Tabla principal
  const [records, setRecords] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [totalRecords, setTotalRecords] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const [searchTerm, setSearchTerm] = useState('');
  const [estadoFilter, setEstadoFilter] = useState('');

  // Modal registrar llegada por consecutivo
  const [isLlegadaOpen, setIsLlegadaOpen] = useState(false);
  const [consecutivoInput, setConsecutivoInput] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [registroResult, setRegistroResult] = useState(null);

  // Modal confirmación llegada (con bolsas)
  const [isConfirmLlegadaOpen, setIsConfirmLlegadaOpen] = useState(false);
  const [llegadaRecord, setLlegadaRecord] = useState(null);
  const [bolsasInput, setBolsasInput] = useState('');
  const [isRegistrandoLlegada, setIsRegistrandoLlegada] = useState(false);

  // Modal verificar
  const [isVerifyOpen, setIsVerifyOpen] = useState(false);
  const [recoleccionToVerify, setRecoleccionToVerify] = useState(null);
  const [verifyItems, setVerifyItems] = useState([]);
  const [observacionVerificacion, setObservacionVerificacion] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);
  const [familiaMap, setFamiliaMap] = useState(new Map()); // prendaId → familia
  const [clienteGarments, setClienteGarments] = useState([]); // prendas del cliente para agregar
  const [isLoadingVerifyDetails, setIsLoadingVerifyDetails] = useState(false);
  const [showAddPrenda, setShowAddPrenda] = useState(false);

  // Modal detalle
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [detailRecord, setDetailRecord] = useState(null);

  const loadRecords = useCallback(async () => {
    setIsLoading(true);
    try {
      const query = {
        'lavanderia.id': idLavanderia,
        tipo: 'Recoleccion',
      };

      if (estadoFilter) {
        query.estado = estadoFilter;
      }

      if (searchTerm) {
        query.$or = [
          { 'cliente.nombre': { $regex: searchTerm, $options: 'i' } },
          { consecutivo: { $regex: searchTerm, $options: 'i' } },
        ];
      }

      const response = await fetchEntregasClientes({
        query,
        limit: itemsPerPage,
        page: currentPage,
      });

      setRecords(response.data || []);
      setTotalRecords(response.totalRecords || 0);
    } catch (error) {
      console.error('Error al cargar recolecciones:', error);
      toast({ title: 'Error', description: 'No se pudieron cargar las recolecciones', variant: 'destructive' });
      setRecords([]);
    } finally {
      setIsLoading(false);
    }
  }, [idLavanderia, currentPage, itemsPerPage, searchTerm, estadoFilter, toast]);

  useEffect(() => {
    loadRecords();
  }, [loadRecords]);

  // --- Registrar llegada ---
  const openLlegadaConfirmModal = (rec) => {
    setLlegadaRecord(rec);
    setBolsasInput('');
    setIsConfirmLlegadaOpen(true);
  };

  const handleConfirmarLlegada = async () => {
    if (!llegadaRecord) return;
    const bolsas = parseInt(bolsasInput);
    if (!bolsas || bolsas < 1) {
      toast({ title: 'Advertencia', description: 'Ingresa un número válido de bolsas', variant: 'destructive' });
      return;
    }

    setIsRegistrandoLlegada(true);
    try {
      const response = await registrarLlegada('', llegadaRecord._id, bolsas);
      if (response.state) {
        toast({ title: 'Llegada registrada', description: `Hora de llegada registrada para ${response.data?.consecutivo} (${bolsas} bolsas)` });
        setIsConfirmLlegadaOpen(false);
        setLlegadaRecord(null);
        setIsLlegadaOpen(false);
        loadRecords();
      } else {
        toast({ title: 'Info', description: response.message, variant: 'destructive' });
      }
    } catch (error) {
      toast({ title: 'Error', description: error.message || 'No se pudo registrar la llegada', variant: 'destructive' });
    } finally {
      setIsRegistrandoLlegada(false);
    }
  };

  const handleRegistrarLlegadaPorConsecutivo = async (idDirecto) => {
    const cons = consecutivoInput.trim();
    if (!cons && !idDirecto) {
      toast({ title: 'Advertencia', description: 'Ingresa un consecutivo', variant: 'destructive' });
      return;
    }

    setIsSearching(true);
    if (!idDirecto) setRegistroResult(null);
    try {
      const response = await fetchEntregasClientes({
        query: {
          consecutivo: cons.toUpperCase(),
          tipo: 'Recoleccion',
          estado: 'Entregado',
        },
        limit: 10,
        page: 1,
      });
      const found = response.data || [];
      if (found.length === 0) {
        setRegistroResult({ success: false, message: 'Recolección no encontrada con ese consecutivo (o ya fue registrada)' });
        toast({ title: 'Info', description: 'Recolección no encontrada', variant: 'destructive' });
      } else if (found.length === 1) {
        // Abrir modal de confirmación con la info de la recolección
        openLlegadaConfirmModal(found[0]);
      } else {
        // Múltiples — mostrar lista para elegir
        setRegistroResult({ multiple: true, records: found });
        toast({ title: 'Múltiples resultados', description: 'Selecciona el cliente correcto' });
      }
    } catch (error) {
      setRegistroResult({ success: false, message: error.message });
      toast({ title: 'Error', description: error.message || 'No se pudo registrar la llegada', variant: 'destructive' });
    } finally {
      setIsSearching(false);
    }
  };

  // --- Verificar ---
  const openVerifyModal = async (rec) => {
    setRecoleccionToVerify(rec);
    setVerifyItems(
      (rec.items || []).map((item) => ({
        ...item,
        cantidad_confirmada: item.cantidad_confirmada || item.cantidad_alistada || 0,
      }))
    );
    setObservacionVerificacion('');
    setShowAddPrenda(false);
    setIsVerifyOpen(true);

    // Cargar familia map y prendas del cliente en paralelo
    setIsLoadingVerifyDetails(true);
    try {
      const clienteId = rec.cliente?.id || rec.cliente?._id;
      const [garmentRes, assignRes] = await Promise.all([
        getClientGarments({ limit: 500 }),
        clienteId ? getClientAssignedGarments(clienteId) : Promise.resolve({ data: [] }),
      ]);

      // Construir mapa prendaId → familia
      const fMap = new Map();
      (garmentRes.data || []).forEach((g) => fMap.set(String(g._id), g.familia || null));
      setFamiliaMap(fMap);

      // Prendas del cliente disponibles para agregar
      setClienteGarments(
        (assignRes.data || []).map((g) => ({
          prendaId: String(g.prendaId || g._id),
          nombre: g.tipoPrenda?.nombre || g.nombre || 'Prenda',
          peso: g.peso || 0,
          familia: fMap.get(String(g.prendaId || g._id)) || null,
        }))
      );
    } catch (e) {
      console.error('Error cargando detalles de verificación:', e);
    } finally {
      setIsLoadingVerifyDetails(false);
    }
  };

  const updateVerifyItemCantidad = (index, value) => {
    setVerifyItems((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], cantidad_confirmada: Math.max(0, value) };
      return updated;
    });
  };

  const addPrendaToVerify = (prenda) => {
    setVerifyItems((prev) => {
      if (prev.some((i) => String(i.prendaId) === String(prenda.prendaId))) return prev;
      return [...prev, {
        prendaId: prenda.prendaId,
        nombre: prenda.nombre,
        peso: prenda.peso || 0,
        familia: prenda.familia,
        cantidad_alistada: 0,
        cantidad_confirmada: 1,
        _esNueva: true,
      }];
    });
  };

  const handleVerificar = async () => {
    setIsVerifying(true);
    try {
      const conductorId = user?.id || user?._id || user?.raw?._id || '';
      const nombreCompleto = ((user?.name || user?.raw?.nombre || '') + ' ' + (user?.raw?.apellido || '')).trim();

      await verificarRecoleccion(
        recoleccionToVerify._id,
        verifyItems.map((item) => ({
          prendaId: item.prendaId,
          cantidad_confirmada: item.cantidad_confirmada,
          // Incluir campos extra para items nuevos que el backend necesita
          ...(item._esNueva ? { nombre: item.nombre, peso: item.peso || 0, cantidad_alistada: 0 } : {}),
        })),
        { id: conductorId, nombre: nombreCompleto || 'Usuario' },
        observacionVerificacion
      );

      toast({ title: 'Verificada', description: `Recolección ${recoleccionToVerify.consecutivo} verificada exitosamente` });
      setIsVerifyOpen(false);
      setRecoleccionToVerify(null);
      loadRecords();
    } catch (error) {
      toast({ title: 'Error', description: error.message || 'No se pudo verificar la recolección', variant: 'destructive' });
    } finally {
      setIsVerifying(false);
    }
  };

  // --- Helpers ---
  const formatDateTime = (dateStr) => {
    if (!dateStr) return '-';
    const d = new Date(dateStr);
    return `${d.toLocaleDateString('es-ES')} ${d.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })}`;
  };

  const getEstadoBadge = (estado) => {
    const estilos = {
      'Entregado': 'bg-blue-100 text-blue-800',
      'Pendiente Verificacion': 'bg-amber-100 text-amber-800',
      'Verificado': 'bg-green-100 text-green-800',
      'Cancelado': 'bg-red-100 text-red-800',
    };
    return estilos[estado] || 'bg-gray-100 text-gray-800';
  };

  const getEstadoLabel = (estado) => {
    const labels = {
      'Entregado': 'Recolectado',
      'Pendiente Verificacion': 'Pend. Verificación',
      'Verificado': 'Verificado',
      'Cancelado': 'Cancelado',
    };
    return labels[estado] || estado;
  };

  // --- Columnas de la tabla ---
  const tableColumns = [
    {
      id: 'actions',
      key: 'actions',
      label: 'Acciones',
      render: (row) => (
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 text-blue-600 hover:text-blue-700"
            title="Ver detalle"
            onClick={() => { setDetailRecord(row); setIsDetailOpen(true); }}
          >
            <Eye className="h-4 w-4" />
          </Button>
          {row.estado === 'Entregado' && (
            <Button
              variant="ghost"
              size="sm"
              className="h-8 text-amber-600 hover:text-amber-700 hover:bg-amber-50 gap-1 text-xs px-2"
              title="Registrar llegada"
              onClick={() => openLlegadaConfirmModal(row)}
            >
              <Clock className="h-3.5 w-3.5" />
              Llegada
            </Button>
          )}
          {row.estado === 'Pendiente Verificacion' && (
            <Button
              variant="ghost"
              size="sm"
              className="h-8 text-green-600 hover:text-green-700 hover:bg-green-50 gap-1 text-xs px-2"
              title="Verificar prendas"
              onClick={() => openVerifyModal(row)}
            >
              <ClipboardCheck className="h-3.5 w-3.5" />
              Verificar
            </Button>
          )}
        </div>
      ),
    },
    {
      id: 'estado',
      label: 'Estado',
      render: (row) => (
        <Badge className={getEstadoBadge(row.estado)}>
          {getEstadoLabel(row.estado)}
        </Badge>
      ),
    },
    {
      id: 'consecutivo',
      label: 'Consecutivo',
      render: (row) => (
        <span className="font-semibold text-amber-700">{row.consecutivo}</span>
      ),
    },
    {
      id: 'cliente',
      label: 'Cliente',
      render: (row) => (
        <div>
          <div className="font-medium">{row.cliente?.nombre}</div>
          <div className="text-xs text-gray-500">{row.cliente?.nit || ''}</div>
        </div>
      ),
    },
    {
      id: 'conductor',
      label: 'Conductor',
      render: (row) => (
        <span className="text-sm">{row.conductor?.nombre || '-'}</span>
      ),
    },
    {
      id: 'totalPrendas',
      label: 'Prendas',
      render: (row) => {
        const total = row.items?.reduce((s, i) => s + (i.cantidad_alistada || 0), 0) || 0;
        return <span className="font-medium">{total}</span>;
      },
    },
    {
      id: 'fecha_recoleccion',
      label: 'Fecha Recolección',
      render: (row) => (
        <span className="text-sm">{formatDateTime(row.fecha_recoleccion || row.fecha_creacion)}</span>
      ),
    },
    {
      id: 'fecha_llegada',
      label: 'Llegada',
      render: (row) => (
        <span className="text-sm">{row.fecha_llegada ? formatDateTime(row.fecha_llegada) : '-'}</span>
      ),
    },
    {
      id: 'fecha_verificacion',
      label: 'Verificación',
      render: (row) => (
        <span className="text-sm">{row.fecha_verificacion ? formatDateTime(row.fecha_verificacion) : '-'}</span>
      ),
    },
  ];

  const estadoOptions = [
    { value: '', label: 'Todos los estados' },
    { value: 'Entregado', label: 'Recolectado (sin llegada)' },
    { value: 'Pendiente Verificacion', label: 'Pendiente Verificación' },
    { value: 'Verificado', label: 'Verificado' },
  ];

  return (
    <div className="p-4 md:p-6 lg:p-8 space-y-6">
      <div className="mb-4">
        <Button asChild variant="outline">
          <Link to={`/centros-lavado/${idLavanderia}`}>
            <ArrowLeft className="mr-2 h-4 w-4" /> Volver al Centro de Lavado
          </Link>
        </Button>
      </div>

      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
      >
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="bg-amber-500/10 p-3 rounded-lg">
                  <ClipboardCheck className="h-8 w-8 text-amber-600" />
                </div>
                <div>
                  <CardTitle className="text-2xl">Verificación recepción ropa sucia</CardTitle>
                  <CardDescription>
                    Registrar llegada y verificar prendas de recolecciones
                  </CardDescription>
                </div>
              </div>
              <Button
                onClick={() => { setIsLlegadaOpen(true); setConsecutivoInput(''); setRegistroResult(null); }}
                className="gap-2 bg-amber-600 hover:bg-amber-700"
              >
                <Clock className="h-4 w-4" />
                Registrar Llegada
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {/* Filtros */}
            <div className="flex flex-col sm:flex-row gap-3 mb-6">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  type="text"
                  placeholder="Buscar por cliente o consecutivo..."
                  value={searchTerm}
                  onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
                  className="pl-10"
                />
              </div>
              <select
                value={estadoFilter}
                onChange={(e) => { setEstadoFilter(e.target.value); setCurrentPage(1); }}
                className="h-10 px-3 rounded-md border border-input bg-background text-sm min-w-[220px]"
              >
                {estadoOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>

            {/* Tabla */}
            <DataTable
              data={records}
              columns={tableColumns}
              isLoading={isLoading}
              page={currentPage}
              limit={itemsPerPage}
              totalRecords={totalRecords}
              onPageChange={setCurrentPage}
              onLimitChange={setItemsPerPage}
            />
          </CardContent>
        </Card>
      </motion.div>

      {/* Modal: Registrar Llegada por Consecutivo */}
      <Dialog open={isLlegadaOpen} onOpenChange={setIsLlegadaOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Clock className="h-5 w-5 text-amber-600" />
              Registrar Llegada por Consecutivo
            </DialogTitle>
            <DialogDescription>
              Ingresa el consecutivo de la recolección (ej: REC-00001) para registrar la hora de llegada
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div className="flex gap-2">
              <Input
                placeholder="REC-00001"
                value={consecutivoInput}
                onChange={(e) => setConsecutivoInput(e.target.value.toUpperCase())}
                className="flex-1"
                onKeyDown={(e) => e.key === 'Enter' && handleRegistrarLlegadaPorConsecutivo()}
                autoFocus
              />
              <Button
                onClick={() => handleRegistrarLlegadaPorConsecutivo()}
                disabled={isSearching || !consecutivoInput.trim()}
                className="bg-amber-600 hover:bg-amber-700 text-white gap-2"
              >
                {isSearching ? <Loader2 className="h-4 w-4 animate-spin" /> : <Clock className="h-4 w-4" />}
                Registrar
              </Button>
            </div>

            {/* Resultado */}
            {registroResult && (
              <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
                {registroResult.multiple ? (
                  <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg space-y-3">
                    <div className="flex items-center gap-2">
                      <AlertCircle className="h-5 w-5 text-amber-600" />
                      <span className="font-semibold text-amber-800">
                        Múltiples clientes con {consecutivoInput.toUpperCase()}
                      </span>
                    </div>
                    <p className="text-sm text-amber-700">Selecciona el cliente correcto:</p>
                    <div className="space-y-2">
                      {registroResult.records.map((rec) => {
                        const total = rec.items?.reduce((s, i) => s + (i.cantidad_alistada || 0), 0) || 0;
                        return (
                          <button
                            key={rec._id}
                            onClick={() => { openLlegadaConfirmModal(rec); }}
                            disabled={isSearching}
                            className="w-full text-left p-3 bg-white border border-amber-200 rounded-lg hover:bg-amber-50 transition-colors flex items-center justify-between"
                          >
                            <div>
                              <p className="font-medium text-sm">{rec.cliente?.nombre}</p>
                              <p className="text-xs text-gray-500">
                                {total} prendas · {formatDateTime(rec.fecha_recoleccion || rec.fecha_creacion)}
                              </p>
                            </div>
                            <Badge className="bg-amber-100 text-amber-800 text-xs">Seleccionar</Badge>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ) : registroResult.success ? (
                  <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                    <div className="flex items-center gap-2 mb-2">
                      <CheckCircle className="h-5 w-5 text-green-600" />
                      <span className="font-semibold text-green-800">Llegada registrada</span>
                    </div>
                    <div className="text-sm text-green-700 space-y-1">
                      <p><strong>Consecutivo:</strong> {registroResult.data?.consecutivo}</p>
                      <p><strong>Cliente:</strong> {registroResult.data?.cliente?.nombre}</p>
                      <p><strong>Hora llegada:</strong> {formatDateTime(registroResult.data?.fecha_llegada)}</p>
                      <p><strong>Prendas:</strong> {registroResult.data?.items?.reduce((s, i) => s + (i.cantidad_alistada || 0), 0)} unidades</p>
                    </div>
                  </div>
                ) : (
                  <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                    <div className="flex items-center gap-2">
                      <AlertCircle className="h-5 w-5 text-red-600" />
                      <span className="text-sm text-red-700">{registroResult.message}</span>
                    </div>
                  </div>
                )}
              </motion.div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsLlegadaOpen(false)}>Cerrar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal: Confirmar Llegada (bolsas) */}
      <Dialog open={isConfirmLlegadaOpen} onOpenChange={(open) => { if (!open) { setIsConfirmLlegadaOpen(false); setLlegadaRecord(null); } }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Clock className="h-5 w-5 text-amber-600" />
              Registrar Llegada
            </DialogTitle>
            <DialogDescription>
              Confirma la información y digita el número de bolsas recibidas
            </DialogDescription>
          </DialogHeader>

          {llegadaRecord && (
            <div className="space-y-4 py-2">
              {/* Info de la recolección */}
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-xs text-gray-500 mb-1">Consecutivo</p>
                  <p className="text-sm font-semibold text-amber-700">{llegadaRecord.consecutivo}</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-xs text-gray-500 mb-1">Cliente</p>
                  <p className="text-sm font-medium">{llegadaRecord.cliente?.nombre}</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-xs text-gray-500 mb-1">Conductor</p>
                  <p className="text-sm font-medium">{llegadaRecord.conductor?.nombre || '-'}</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-xs text-gray-500 mb-1">Fecha recolección</p>
                  <p className="text-sm font-medium">{formatDateTime(llegadaRecord.fecha_recoleccion || llegadaRecord.fecha_creacion)}</p>
                </div>
              </div>

              {/* Resumen de prendas */}
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                <p className="text-xs text-amber-600 mb-1">Prendas recolectadas</p>
                <p className="text-lg font-bold text-amber-800">
                  {llegadaRecord.items?.reduce((s, i) => s + (i.cantidad_alistada || 0), 0) || 0} unidades
                </p>
                <div className="mt-2 space-y-1">
                  {(llegadaRecord.items || []).map((item, idx) => (
                    <div key={idx} className="flex justify-between text-xs text-amber-700">
                      <span>{item.nombre}</span>
                      <span className="font-medium">{item.cantidad_alistada}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Input de bolsas */}
              <div>
                <Label htmlFor="bolsas-input" className="text-sm font-semibold">
                  Número de bolsas/carros recibidas <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="bolsas-input"
                  type="number"
                  placeholder="Ej: 3"
                  value={bolsasInput}
                  onChange={(e) => setBolsasInput(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleConfirmarLlegada()}
                  min={1}
                  className="mt-1 text-lg h-12 text-center font-semibold"
                  autoFocus
                />
              </div>
            </div>
          )}

          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => { setIsConfirmLlegadaOpen(false); setLlegadaRecord(null); }} disabled={isRegistrandoLlegada}>
              Cancelar
            </Button>
            <Button
              onClick={handleConfirmarLlegada}
              disabled={isRegistrandoLlegada || !bolsasInput}
              className="gap-2 bg-amber-600 hover:bg-amber-700"
            >
              {isRegistrandoLlegada ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Registrando...
                </>
              ) : (
                <>
                  <CheckCircle className="h-4 w-4" />
                  Confirmar Llegada
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal: Verificar Recolección */}
      <Dialog open={isVerifyOpen} onOpenChange={setIsVerifyOpen}>
        <DialogContent className="sm:max-w-3xl max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ClipboardCheck className="h-5 w-5 text-amber-600" />
              Verificar Recolección
            </DialogTitle>
            <DialogDescription>
              {recoleccionToVerify?.consecutivo} — {recoleccionToVerify?.cliente?.nombre}
            </DialogDescription>
          </DialogHeader>

          {recoleccionToVerify && (
            <div className="flex-1 overflow-y-auto space-y-4 pr-1">
              {/* Info */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-xs text-gray-500 mb-1">Conductor</p>
                  <p className="text-sm font-medium">{recoleccionToVerify.conductor?.nombre || '-'}</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-xs text-gray-500 mb-1">Hora llegada</p>
                  <p className="text-sm font-medium">{formatDateTime(recoleccionToVerify.fecha_llegada)}</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-xs text-gray-500 mb-1">Fecha recolección</p>
                  <p className="text-sm font-medium">{formatDateTime(recoleccionToVerify.fecha_recoleccion)}</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-xs text-gray-500 mb-1">Total prendas</p>
                  <p className="text-sm font-medium">{verifyItems.reduce((s, i) => s + (i.cantidad_alistada || 0), 0)}</p>
                </div>
              </div>

              {/* Items agrupados por familia */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label className="text-sm font-semibold">Prendas — ajustar cantidades</Label>
                  {isLoadingVerifyDetails && <Loader2 className="h-4 w-4 animate-spin text-amber-500" />}
                </div>

                {/* Grupos por familia */}
                {(() => {
                  const groups = new Map();
                  verifyItems.forEach((item, idx) => {
                    const familia = item.familia?.nombre || familiaMap.get(String(item.prendaId))?.nombre || 'Sin Familia';
                    if (!groups.has(familia)) groups.set(familia, []);
                    groups.get(familia).push({ ...item, _idx: idx });
                  });
                  const sorted = Array.from(groups.entries()).sort(([a], [b]) => {
                    if (a === 'Sin Familia') return 1;
                    if (b === 'Sin Familia') return -1;
                    return a.localeCompare(b);
                  });
                  return sorted.map(([familiaName, items]) => {
                    const totalAlistada = items.reduce((s, i) => s + (i.cantidad_alistada || 0), 0);
                    const totalConfirmada = items.reduce((s, i) => s + (i.cantidad_confirmada || 0), 0);
                    const diffGrupo = totalConfirmada - totalAlistada;
                    return (
                      <div key={familiaName} className="mb-3 border rounded-lg overflow-hidden">
                        {/* Cabecera familia */}
                        <div className="flex items-center justify-between px-4 py-2 bg-amber-50 border-b border-amber-200">
                          <div className="flex items-center gap-2">
                            <div className="h-2.5 w-2.5 rounded-full bg-amber-500" />
                            <span className="text-xs font-bold text-amber-800 uppercase tracking-wide">{familiaName}</span>
                          </div>
                          <div className="flex items-center gap-3 text-xs">
                            <span className="text-gray-500">Recol: <strong>{totalAlistada}</strong></span>
                            <span className="text-amber-700">Verif: <strong>{totalConfirmada}</strong></span>
                            {diffGrupo !== 0 && (
                              <span className={`font-bold ${diffGrupo > 0 ? 'text-green-600' : 'text-red-600'}`}>
                                {diffGrupo > 0 ? '+' : ''}{diffGrupo}
                              </span>
                            )}
                          </div>
                        </div>
                        {/* Items del grupo */}
                        <table className="w-full text-sm">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="text-left px-4 py-1.5 font-medium text-gray-500 text-xs">Prenda</th>
                              <th className="text-center px-3 py-1.5 font-medium text-gray-500 text-xs">Recol.</th>
                              <th className="text-center px-3 py-1.5 font-medium text-gray-500 text-xs">Verificadas</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y">
                            {items.map((item) => {
                              const diff = item.cantidad_confirmada - (item.cantidad_alistada || 0);
                              return (
                                <tr key={item._idx} className={`hover:bg-gray-50/50 ${item._esNueva ? 'bg-green-50/40' : ''}`}>
                                  <td className="px-4 py-2">
                                    <span className="font-medium">{item.nombre}</span>
                                    {item._esNueva && (
                                      <Badge className="ml-2 text-[10px] bg-green-100 text-green-700 px-1.5 py-0">Nueva</Badge>
                                    )}
                                  </td>
                                  <td className="px-3 py-2 text-center text-gray-400 text-sm">
                                    {item.cantidad_alistada || 0}
                                  </td>
                                  <td className="px-3 py-2">
                                    <div className="flex items-center justify-center gap-1">
                                      <Button variant="outline" size="icon" className="h-7 w-7" onClick={() => updateVerifyItemCantidad(item._idx, item.cantidad_confirmada - 1)}>
                                        <Minus className="h-3 w-3" />
                                      </Button>
                                      <Input
                                        type="number"
                                        className={`w-14 h-7 text-center text-sm ${diff !== 0 ? 'border-amber-400 bg-amber-50' : ''}`}
                                        value={item.cantidad_confirmada}
                                        onChange={(e) => updateVerifyItemCantidad(item._idx, parseInt(e.target.value) || 0)}
                                        min={0}
                                      />
                                      <Button variant="outline" size="icon" className="h-7 w-7" onClick={() => updateVerifyItemCantidad(item._idx, item.cantidad_confirmada + 1)}>
                                        <Plus className="h-3 w-3" />
                                      </Button>
                                      {diff !== 0 && (
                                        <span className={`text-xs font-bold ml-1 ${diff > 0 ? 'text-green-600' : 'text-red-600'}`}>
                                          {diff > 0 ? '+' : ''}{diff}
                                        </span>
                                      )}
                                    </div>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    );
                  });
                })()}

                {/* Panel agregar prenda */}
                <div className="border rounded-lg overflow-hidden mt-2">
                  <button
                    type="button"
                    className="w-full flex items-center justify-between px-4 py-2.5 bg-gray-50 hover:bg-gray-100 transition-colors"
                    onClick={() => setShowAddPrenda((v) => !v)}
                  >
                    <div className="flex items-center gap-2 text-sm font-semibold text-gray-700">
                      <PlusCircle className="h-4 w-4 text-green-600" />
                      Agregar prenda no recolectada
                    </div>
                    {showAddPrenda ? <ChevronDown className="h-4 w-4 text-gray-400" /> : <ChevronRight className="h-4 w-4 text-gray-400" />}
                  </button>
                  {showAddPrenda && (
                    <div className="p-3 space-y-1 max-h-52 overflow-y-auto">
                      {isLoadingVerifyDetails ? (
                        <div className="flex items-center justify-center py-4">
                          <Loader2 className="h-5 w-5 animate-spin text-amber-500" />
                        </div>
                      ) : clienteGarments.filter((g) => !verifyItems.some((vi) => String(vi.prendaId) === String(g.prendaId))).length === 0 ? (
                        <p className="text-xs text-gray-400 text-center py-3">Todas las prendas del cliente ya están en la lista</p>
                      ) : (
                        clienteGarments
                          .filter((g) => !verifyItems.some((vi) => String(vi.prendaId) === String(g.prendaId)))
                          .map((g) => (
                            <div key={g.prendaId} className="flex items-center justify-between text-sm p-2 rounded hover:bg-gray-50">
                              <div>
                                <span className="font-medium">{g.nombre}</span>
                                {g.familia?.nombre && (
                                  <span className="ml-2 text-[10px] text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded">{g.familia.nombre}</span>
                                )}
                              </div>
                              <Button
                                size="sm"
                                variant="outline"
                                className="h-7 gap-1 text-xs text-green-700 border-green-300 hover:bg-green-50"
                                onClick={() => addPrendaToVerify(g)}
                              >
                                <Plus className="h-3 w-3" /> Agregar
                              </Button>
                            </div>
                          ))
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Observaciones */}
              <div>
                <Label htmlFor="obs-verify">Observaciones (opcional)</Label>
                <Input
                  id="obs-verify"
                  placeholder="Observaciones de la verificación..."
                  value={observacionVerificacion}
                  onChange={(e) => setObservacionVerificacion(e.target.value)}
                  className="mt-1"
                />
              </div>
            </div>
          )}

          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setIsVerifyOpen(false)} disabled={isVerifying}>
              Cancelar
            </Button>
            <Button onClick={handleVerificar} disabled={isVerifying} className="gap-2 bg-amber-600 hover:bg-amber-700">
              {isVerifying ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Verificando...
                </>
              ) : (
                <>
                  <CheckCircle className="h-4 w-4" />
                  Confirmar Verificación
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal: Detalle de Recolección */}
      <Dialog open={isDetailOpen} onOpenChange={setIsDetailOpen}>
        <DialogContent className="sm:max-w-4xl max-h-[90vh]  overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Package className="h-5 w-5 text-amber-600" />
              Detalle Recolección — {detailRecord?.consecutivo || 'N/A'}
            </DialogTitle>
            <DialogDescription>
              Cliente: {detailRecord?.cliente?.nombre || 'N/A'}
            </DialogDescription>
          </DialogHeader>

          {detailRecord && (
            <div className="space-y-4 mt-2">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-xs text-gray-500 mb-1">Estado</p>
                  <Badge className={getEstadoBadge(detailRecord.estado)}>{getEstadoLabel(detailRecord.estado)}</Badge>
                </div>
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-xs text-gray-500 mb-1">Conductor</p>
                  <p className="text-sm font-medium">{detailRecord.conductor?.nombre || '-'}</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-xs text-gray-500 mb-1">Recibe</p>
                  <p className="text-sm font-medium">{detailRecord.recibe?.nombre || '-'}</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-xs text-gray-500 mb-1">Fecha recolección</p>
                  <p className="text-sm font-medium">{formatDateTime(detailRecord.fecha_recoleccion || detailRecord.fecha_creacion)}</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-xs text-gray-500 mb-1">Llegada</p>
                  <p className="text-sm font-medium">{detailRecord.fecha_llegada ? formatDateTime(detailRecord.fecha_llegada) : '-'}</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-xs text-gray-500 mb-1">Verificación</p>
                  <p className="text-sm font-medium">{detailRecord.fecha_verificacion ? formatDateTime(detailRecord.fecha_verificacion) : '-'}</p>
                </div>
              </div>

              {detailRecord.verificado_por?.nombre && (
                <div className="bg-green-50 rounded-lg p-3 border border-green-200">
                  <p className="text-xs text-green-600 mb-1">Verificado por</p>
                  <p className="text-sm font-medium text-green-800">{detailRecord.verificado_por.nombre}</p>
                </div>
              )}

              <div>
                <Label className="text-sm font-semibold mb-2 block">Prendas</Label>
                <div className="border rounded-lg overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="text-left px-4 py-2 font-semibold">Prenda</th>
                        <th className="text-center px-4 py-2 font-semibold">Recolectadas</th>
                        <th className="text-center px-4 py-2 font-semibold">Verificadas</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {(detailRecord.items || []).map((item, idx) => (
                        <tr key={idx}>
                          <td className="px-4 py-2 font-medium">{item.nombre}</td>
                          <td className="px-4 py-2 text-center">{item.cantidad_alistada}</td>
                          <td className="px-4 py-2 text-center">
                            {item.cantidad_confirmada ?? '-'}
                            {item.cantidad_confirmada != null && item.cantidad_confirmada !== item.cantidad_alistada && (
                              <span className={`ml-1 text-xs font-medium ${item.cantidad_confirmada > item.cantidad_alistada ? 'text-green-600' : 'text-red-600'}`}>
                                ({item.cantidad_confirmada > item.cantidad_alistada ? '+' : ''}{item.cantidad_confirmada - item.cantidad_alistada})
                              </span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {detailRecord.observaciones && (
                <div>
                  <Label className="text-sm font-semibold">Observaciones</Label>
                  <p className="text-sm text-gray-600 mt-1">{detailRecord.observaciones}</p>
                </div>
              )}
              {detailRecord.observacion_verificacion && (
                <div>
                  <Label className="text-sm font-semibold">Observaciones de verificación</Label>
                  <p className="text-sm text-gray-600 mt-1">{detailRecord.observacion_verificacion}</p>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default VerificacionRopaSuciaPage;
